// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetDealersApiParameter } from "@insite/client-framework/Services/DealerService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { DealerModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import LocationContentLink from "@insite/content-library/Components/LocationContentLink";
import LocationPagination from "@insite/content-library/Components/LocationPagination";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import MapPin from "@insite/mobius/Icons/MapPin";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import FancyScroll, { FancyScrollRefProps } from "@keller/App/Components/Common/FancyScroll";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import ContactUsFormModal, { ContactUsLocationModalState } from "@keller/App/Components/ContactUs/ContactUsFormModal";
import ShowroomModal from "@keller/App/Components/Locations/ShowroomModal";
import Notify from "@keller/App/Components/Notify";
import Chat from "@keller/XDL/Icons/Chat";
import Clock from "@keller/XDL/Icons/Clock";
import Search from "@keller/XDL/Icons/Search";
import Showroom from "@keller/XDL/Icons/Showroom";
import { color, mergeWithCss, parseBoolean, pluralize, styledScrollbar, xMask, xScreen } from "@keller/XDL/Utilities";
import { iconLinkCommonStyles } from "@keller/XDL/Utilities/links";
import { hideElement } from "@keller/XDL/Utilities/sharedStyles";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: LocationSearchFormStyles;
    onSearch: (
        searchGeoLocationFilter: string,
        searchLocationFilter: string,
        radiusSearch?: number,
        searchIndustryFilter?: string,
        searchShowroomFilter?: string,
    ) => void;
    onLocationSelected: (location: LocationModel) => void;
    locationKnown: boolean;
    showSelectedLocation: boolean;
    getLocationNumberFromPagination: (locationIndex: number) => number;
    openLocationContentDisplay: (location: LocationModel) => void;
    resultCount: number;
    locationSearchFilter: string;
    radiusSearchFilter?: number;
    geoLocationSearchText: string;
    showGeoLocationErrorMessage: boolean;
    geoLocationErrorMessage: React.ReactNode;
    isLoading: boolean;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    setDistanceUnitOfMeasure: (uom: DistanceUnitOfMeasure) => void;
    locations: LocationModel[];
    locationsPagination: PaginationModel | undefined;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    siteMessageResultsErrorMessage: React.ReactNode;
    // XNG-Changes
    industries?: string[];
    setIndustrySelected?: (industry: string) => void;
    setShowroomSelected?: (isSelected: boolean) => void;
    hasShowroomLocations?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    settings: getSettingsCollection(state),
    allLocationsPageUrl: getPageLinkByPageType(state, "AllLocationsPage")?.url,
});

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LocationSearchFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    form?: InjectableCss;
    searchContainer?: InjectableCss;
    searchInputRow?: InjectableCss;
    searchInputGridContainer: GridContainerProps;
    searchGeoLocationGridItem?: GridItemProps;
    searchGeoLocationText?: TextFieldPresentationProps;
    searchLocationsGridItem?: GridItemProps;
    searchLocationsText?: TextFieldPresentationProps;
    searchRadiusGridItem?: GridItemProps;
    searchRadiusText?: TextFieldPresentationProps;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchResultsTextGridItem?: GridItemProps;
    searchResultsText?: TypographyPresentationProps;
    distanceUnitOfMeasureGridItem?: GridItemProps;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureImperialRadio?: RadioComponentProps;
    distanceUnitOfMeasureMetricRadio?: RadioComponentProps;
    siteMessageGridItem?: GridItemProps;
    siteMessageGeocodeErrorText?: TypographyPresentationProps;
    siteMessageResultsErrorText?: TypographyPresentationProps;
    searchResultGridItem?: GridItemProps;
    searchResultRow?: InjectableCss;
    searchResultGridContainer?: GridContainerProps;
    searchResultLocationDetailsGridContainer?: GridContainerProps;
    searchResultInformationGridItem?: GridItemProps;
    searchResultInformationGridContainer?: GridContainerProps;
    searchResultDisplayNumberGridItem?: GridItemProps;
    locationDisplayNumberText?: TypographyPresentationProps;
    searchResultLocationGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    locationNameLink?: LinkPresentationProps;
    searchResultAddressInfoGridItem?: GridItemProps;
    locationAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    searchResultLinksAndDistanceContainer?: GridContainerProps;
    searchResultPhoneGridItem?: GridItemProps;
    searchResultPhoneLink?: TypographyPresentationProps;
    searchResultContentGridItem?: GridItemProps;
    locationContentLink?: LinkPresentationProps;
    searchResultGoogleMapsDirectionLinkGridItem?: GridItemProps;
    locationGoogleMapsDirectionLink?: LinkPresentationProps;
    searchResultDistanceGridItem?: GridItemProps;
    locationDistanceDisplayText?: TypographyPresentationProps;
    searchResultDividerGridItem?: GridItemProps;
    searchResultDivider?: InjectableCss;
    paginationRow?: GridItemProps;
    pagination?: PaginationPresentationProps;
    searchContent?: InjectableCss;
    searchGridContainer?: GridContainerProps;
    paginationGridItem?: GridItemProps;
    customSearchResultLinksOffsetGridItem?: GridItemProps;
    // Xng Changes
    searchIndustryGridItem?: GridItemProps;
    searchCityOrNameGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    allLocationLink?: LinkProps;
    viewAllLocationsWrapper?: InjectableCss;
    searchResultWrapper?: InjectableCss;
    contactUsGridItem?: GridItemProps;
    showroomInfoGridItem?: GridItemProps;
    showroomInfoLink?: LinkPresentationProps;
}

// XNG-Changes: change widget style
export const locationSearchFormStyles: LocationSearchFormStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            padding-top: 100px;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex: 1 1 100%;
            margin-bottom: 5px;
            padding: 10px;
        `,
    },
    searchInputGridContainer: {
        gap: 20,
        css: css`
            ${styledBoxStyle}
        `,
    },
    searchGeoLocationGridItem: {
        width: [12, 12, 5, 5, 4],
    },
    searchLocationsGridItem: {
        width: [12, 12, 5, 4, 2.5] as any,
    },
    searchRadiusGridItem: {
        width: [12, 12, 2, 2, 1.5] as any,
    },
    searchIndustryGridItem: {
        width: [12, 12, 4, 3, 2],
    },
    searchCityOrNameGridItem: {
        width: [12, 12, 4, 3, 2],
    },
    searchGeoLocationText: { iconProps: { src: Search, color: "common.ui7" } },
    searchLocationsText: { iconProps: { src: Search, color: "common.ui7" } },
    searchButtonGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            &&& {
                margin-left: auto;
                flex-basis: auto;
            }
        `,
    },
    searchButton: {
        sizeVariant: "large",
        css: css`
            margin-top: 20px;
            max-width: auto;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    searchResultsText: {
        weight: "bold",
    },
    distanceUnitOfMeasureGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: space-between;
            flex-wrap: wrap;
            &&& {
                margin-right: 5px;
            }
            ${styledBoxStyle}
        `,
    },
    distanceUnitOfMeasureRadioGroup: {
        css: css`
            & > legend {
                display: none;
            }
            & > div {
                margin-top: 0 !important;
            }
        `,
    },
    siteMessageGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-right: 5px;
            }

            &:empty {
                display: none;
            }
            > div {
                width: 100%;
            }
            > span {
                max-width: 100%;
                width: 100%;
                margin: 20px 0 0;
            }
        `,
    },
    searchResultGridItem: {
        width: 12,
    },
    searchResultGridContainer: {
        gap: 0,
        css: css`
            flex-grow: unset;
            padding: 5px 5px 0 5px;
            margin-bottom: 25px;
            ${styledBoxStyle}
        `,
    },
    searchResultDisplayNumberGridItem: {
        width: 1,
        css: css`
            justify-content: center;
        `,
    },
    locationDisplayNumberText: {
        color: "primary",
        weight: "bold",
    },
    searchResultInformationGridItem: {
        width: 11,
    },
    searchResultInformationGridContainer: {
        gap: 10,
    },
    searchResultLocationGridItem: {
        width: 11,
    },
    locationNameLink: {
        color: "primary",
        typographyProps: {
            weight: "bold",
            ellipsis: true,
        },
        css: css`
            margin-bottom: 10px;
        `,
    },
    searchResultAddressInfoGridItem: {
        width: 11,
        css: css`
            address p {
                font-size: 12px;
            }
        `,
    },
    searchResultPhoneGridItem: {
        width: 6,
    },
    locationContentLink: {
        ...iconLinkCommonStyles(Clock),
    },
    searchResultDistanceGridItem: {
        width: 6,
    },
    locationDistanceDisplayText: {
        size: 12,
        lineHeight: "18px",
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: center;
            `,
            perPageSelect: css`
                ${hideElement}
            `,
            linkList: css`
                & .paginationTooltip {
                    font-size: 20px;
                }
            `,
        },
    },
    locationGoogleMapsDirectionLink: {
        ...iconLinkCommonStyles(MapPin),
    },
    searchContent: {
        css: css`
            flex: 0 0 450px;
            padding: 10px;
            ${xScreen("lte", "md")} {
                flex-grow: 1;
            }
            ${xScreen("lte", "sm")} {
                flex: 1 1 100%;
            }
        `,
    },
    searchGridContainer: {
        gap: 0,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            ${xScreen("lte", "md")} {
                &&& {
                    margin-top: 16px;
                }
            }
        `,
    },
    customSearchResultLinksOffsetGridItem: {
        width: [1, 0, 0, 0, 0],
    },
    actionsGridItem: {
        width: 12,
        css: css`
            padding-top: 15px;
        `,
    },
    searchResultPhoneLink: {
        css: css`
            padding-top: 10px;
            font-size: 12px;
            font-weight: bold;
            color: ${color("text.link")};
        `,
    },
    viewAllLocationsWrapper: {
        css: css`
            width: 100%;
            margin-top: 20px;
        `,
    },
    allLocationLink: {
        ...iconLinkCommonStyles(MapPin),
    },
    searchResultWrapper: {
        css: css`
            width: 100%;
            max-height: 586px;
            margin-top: 25px;
            padding-right: 5px;
            ${styledScrollbar(5)}
            ${xScreen("lte", "sm")} {
                height: auto;
            }
        `,
    },
    searchResultContentGridItem: {
        width: 6,
    },
    searchResultGoogleMapsDirectionLinkGridItem: {
        width: 6,
    },
    contactUsGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            ${xScreen("lte", 370)} {
                flex-basis: 50%;
                max-width: 50%;
            }
        `,
    },
    showroomInfoGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            ${xScreen("lte", 370)} {
                flex-basis: 50%;
                max-width: 50%;
            }
        `,
    },
    showroomInfoLink: {
        ...mergeWithCss(iconLinkCommonStyles(Showroom), {
            icon: {
                iconProps: {
                    width: 18,
                },
            },
        }),
        css: css`
            && {
                justify-content: center;
            }
        `,
    },
};

const StyledForm = getStyledWrapper("form");
const StyledHr = getStyledWrapper("hr");

const Container = StyledWrapper;
const ContainerCell = StyledWrapper;

const LocationSearchForm: React.FC<Props> = ({
    extendedStyles,
    onSearch,
    onLocationSelected,
    locationKnown,
    getLocationNumberFromPagination,
    openLocationContentDisplay,
    resultCount,
    locationSearchFilter,
    radiusSearchFilter,
    geoLocationSearchText,
    showGeoLocationErrorMessage,
    geoLocationErrorMessage,
    isLoading,
    distanceUnitOfMeasure,
    setDistanceUnitOfMeasure,
    locations,
    locationsPagination,
    setPage,
    setPageSize,
    siteMessageResultsErrorMessage,
    // XNG-Changes
    industries,
    allLocationsPageUrl,
    setIndustrySelected,
    setShowroomSelected,
    hasShowroomLocations,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationSearchFormStyles, extendedStyles));
    const [searchLocationFilter, setSearchLocationFilter] = React.useState(locationSearchFilter);
    const [searchRadiusFilter, setSearchRadiusFilter] = React.useState(radiusSearchFilter);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);

    // XNG-Changes
    const [searchIndustryFilter, setSearchIndustryFilter] = React.useState("");
    const [searchShowroomFilter, setSearchShowroomFilter] = React.useState("");
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    const [modalOpen, setIsModalOpen] = React.useState<{
        isOpen?: boolean;
        dealer?: DealerModel;
    }>({});

    React.useEffect(() => {
        setSearchLocationFilter(locationSearchFilter);
    }, [locationSearchFilter]);

    React.useEffect(() => {
        setSearchRadiusFilter(radiusSearchFilter);
    }, [radiusSearchFilter]);

    React.useEffect(() => {
        setSearchGeoLocationFilter(geoLocationSearchText);
    }, [geoLocationSearchText]);

    React.useEffect(() => {
        fancyScrollRef.current?.updateScrollable();
    }, [locations]);

    const handleDistanceUnitOfMeasureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as DistanceUnitOfMeasure;
        setDistanceUnitOfMeasure(value);
    };

    const generateOpenLocationContentFor = (location: LocationModel) => {
        return () => openLocationContentDisplay(location);
    };

    const generateLocationSelectClickHandler = (
        event: React.MouseEvent<Element, MouseEvent>,
        location: LocationModel,
    ) => {
        event.preventDefault();
        onLocationSelected(location);
    };

    const isNotFinalResult = (resultIndex: number) => {
        if (!locationsPagination) {
            return false;
        }
        return locationsPagination.pageSize > resultIndex + 1;
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isLoading) {
            locationsPagination && setPage(1);
            onSearch(
                searchGeoLocationFilter,
                searchLocationFilter,
                searchRadiusFilter,
                searchIndustryFilter,
                searchShowroomFilter,
            );
        }
    };

    const handleSearchGeoLocationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGeoLocationFilter(event.target.value);
    };

    const handleSearchLocationsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchLocationFilter(event.target.value);
    };

    const handleSearchRadiusChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchRadiusFilter(Number(event.target.value));
    };

    // XNG-Changes
    const handleIndustryFilterDealers = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSearchIndustryFilter(event.currentTarget.value);
        setIndustrySelected?.(event.currentTarget.value);
        setPage(1);
    };

    const handleShowroomFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSearchShowroomFilter(event.currentTarget.value);
        event.currentTarget.value.toLocaleLowerCase() === "showroom"
            ? setShowroomSelected?.(true)
            : setShowroomSelected?.(false);
        setPage(1);
    };

    const [contactUsLocationModal, setContactUsLocationModal] = React.useState<ContactUsLocationModalState>({
        location: undefined,
    });

    const contactUsLinkClickHandler = (location?: LocationModel | DealerModel) => {
        setContactUsLocationModal({
            location,
        });
    };

    const modalCloseHandler = () => {
        setContactUsLocationModal({
            location: undefined,
        });
    };

    const showroomInfoHandler = (delaer: DealerModel) => {
        setIsModalOpen({
            isOpen: true,
            dealer: delaer,
        });
    };

    const showroomModalCloseHandler = () => {
        setIsModalOpen({
            isOpen: false,
            dealer: undefined,
        });
    };

    // XNG-Changes: update structure
    return (
        <>
            <StyledWrapper {...styles.searchContainer}>
                <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
                    <GridContainer {...styles.searchInputGridContainer}>
                        <GridItem {...styles.searchGeoLocationGridItem}>
                            <TextField
                                id="searchGeoLocation"
                                {...styles.searchGeoLocationText}
                                label={translate("Search by Postal Code, Province or Country")}
                                value={searchGeoLocationFilter}
                                onChange={handleSearchGeoLocationChanged}
                                placeholder={translate("Search by Postal Code, Province or Country")}
                                data-test-selector="locationSearchForm_searchGeoLocation"
                                error={showGeoLocationErrorMessage ? geoLocationErrorMessage : undefined}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.searchLocationsGridItem}>
                            <TextField
                                id="searchLocations"
                                {...styles.searchLocationsText}
                                label={translate("Search for Location")}
                                value={searchLocationFilter}
                                onChange={handleSearchLocationsChanged}
                                placeholder={translate("Search for Location")}
                                data-test-selector="locationSearchForm_searchLocations"
                            />
                        </GridItem>
                        <GridItem {...styles.searchRadiusGridItem}>
                            <TextField
                                id="searchRadius"
                                {...styles.searchRadiusText}
                                label={translate("Search Radius")}
                                value={searchRadiusFilter}
                                onChange={handleSearchRadiusChanged}
                                type="number"
                                min={0}
                                placeholder={translate("Search Radius")}
                            />
                        </GridItem>
                        <GridItem {...styles.searchIndustryGridItem}>
                            <Select
                                placeholder={translate("Filter by Industry")}
                                label={translate("Filter by Industry")}
                                onChange={handleIndustryFilterDealers}
                            >
                                <option value="">{translate("Filter by Industry")}</option>
                                {industries?.map(industry => (
                                    <option key={industry} value={industry}>
                                        {industry}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        {hasShowroomLocations && (
                            <GridItem {...styles.searchCityOrNameGridItem}>
                                <Select
                                    placeholder={translate("Filter by Locations")}
                                    label={translate("Filter by Location")}
                                    onChange={handleShowroomFilter}
                                >
                                    <option value="All">{translate("All")}</option>
                                    <option value="Showroom">{translate("Showroom")}</option>
                                </Select>
                            </GridItem>
                        )}
                        <GridItem {...styles.searchButtonGridItem}>
                            <Button
                                {...styles.searchButton}
                                type="submit"
                                data-test-selector="locationSearchForm_searchButton"
                            >
                                {translate("Search")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </StyledWrapper>
            <StyledWrapper {...styles.searchContent}>
                <GridContainer {...styles.searchGridContainer}>
                    <GridItem {...styles.distanceUnitOfMeasureGridItem}>
                        <Typography {...styles.searchResultsText}>
                            {translate(pluralize(resultCount, "Result", { showNumber: true }))}
                        </Typography>
                        <RadioGroup
                            {...styles.distanceUnitOfMeasureRadioGroup}
                            id="distanceUoM"
                            name="distanceUoM"
                            value={distanceUnitOfMeasure}
                            onChangeHandler={handleDistanceUnitOfMeasureChange}
                            horizontal
                        >
                            <Radio {...styles.distanceUnitOfMeasureImperialRadio} value="Imperial">
                                {translate("Miles")}
                            </Radio>
                            <Radio {...styles.distanceUnitOfMeasureMetricRadio} value="Metric">
                                {translate("Kilometers")}
                            </Radio>
                        </RadioGroup>
                        {allLocationsPageUrl && (
                            <StyledWrapper {...styles.viewAllLocationsWrapper}>
                                <Link {...styles.allLocationLink} href={allLocationsPageUrl}>
                                    {translate("View All Locations")}
                                </Link>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem {...styles.siteMessageGridItem}>
                        {!isLoading && !locationKnown && geoLocationSearchText.length > 0 && (
                            <Notify type="danger">{siteMessage("PickUpLocation_GeocodeErrorMessage")}</Notify>
                        )}
                        {!isLoading && locationKnown && locations.length === 0 && siteMessageResultsErrorMessage && (
                            <Notify type="danger" data-test-selector="locationSearchForm_siteErrorMessage">
                                {siteMessageResultsErrorMessage}
                            </Notify>
                        )}
                    </GridItem>
                    {isLoading && locations.length === 0 && (
                        <GridItem {...styles.searchResultGridItem}>
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        </GridItem>
                    )}
                    <GridItem {...styles.searchResultGridItem}>
                        <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                            <StyledWrapper ref={parentBoxRef} {...styles.searchResultWrapper}>
                                {locations?.map((location, index) => (
                                    <GridContainer
                                        {...styles.searchResultGridContainer}
                                        data-test-selector="locationSearchForm_location"
                                        data-test-key={`${location.id}`}
                                        key={location.id}
                                    >
                                        <GridItem {...styles.searchResultInformationGridItem}>
                                            <GridContainer {...styles.searchResultInformationGridContainer}>
                                                <GridItem {...styles.searchResultLocationGridItem}>
                                                    <Link
                                                        {...styles.locationNameLink}
                                                        onClick={(event: React.MouseEvent<Element, MouseEvent>) =>
                                                            generateLocationSelectClickHandler(event, location)
                                                        }
                                                        data-test-selector="findLocationModal_locationSelect"
                                                        data-markerNumber={index + 1}
                                                    >
                                                        {location.name}
                                                    </Link>
                                                </GridItem>
                                                <GridItem {...styles.searchResultAddressInfoGridItem}>
                                                    <AddressInfoCondensedDisplay
                                                        {...styles.locationAddressInfoCondensed}
                                                        {...location}
                                                    />
                                                </GridItem>
                                                {/* XNG-Changes */}
                                                {location.phone && (
                                                    <GridItem {...styles.searchResultPhoneGridItem}>
                                                        <ContactTypography
                                                            type="phone"
                                                            targetValue={location.phone}
                                                            icon={true}
                                                            {...styles.searchResultPhoneLink}
                                                        >
                                                            {xMask.phoneNumber(location.phone)}
                                                        </ContactTypography>
                                                    </GridItem>
                                                )}
                                                <GridItem {...styles.searchResultDistanceGridItem}>
                                                    <DistanceDisplay
                                                        {...styles.locationDistanceDisplayText}
                                                        distance={location.distance}
                                                        unitOfMeasure={distanceUnitOfMeasure}
                                                    />
                                                </GridItem>
                                                {/* XNG-Changes */}
                                                <GridItem {...styles.actionsGridItem}>
                                                    <GridContainer gap={12}>
                                                        <GridItem {...styles.contactUsGridItem}>
                                                            <Link
                                                                onClick={() => contactUsLinkClickHandler(location)}
                                                                data-test-selector="contactUsLinkClick"
                                                                {...iconLinkCommonStyles(Chat)}
                                                            >
                                                                {translate("Contact Us")}
                                                            </Link>
                                                        </GridItem>
                                                        <GridItem
                                                            {...styles.searchResultGoogleMapsDirectionLinkGridItem}
                                                        >
                                                            <GoogleMapsDirectionLink
                                                                {...styles.locationGoogleMapsDirectionLink}
                                                                {...location}
                                                            />
                                                        </GridItem>
                                                        {parseBoolean(
                                                            (location as DealerModel)?.properties?.isShowroomLocation,
                                                        ) && (
                                                            <GridItem {...styles.showroomInfoGridItem}>
                                                                <Link
                                                                    {...styles.showroomInfoLink}
                                                                    onClick={() =>
                                                                        showroomInfoHandler(location as DealerModel)
                                                                    }
                                                                >
                                                                    {translate("Showroom")}
                                                                </Link>
                                                            </GridItem>
                                                        )}
                                                        {location.htmlContent && (
                                                            <GridItem {...styles.searchResultContentGridItem}>
                                                                <LocationContentLink
                                                                    {...styles.locationContentLink}
                                                                    location={location}
                                                                    onOpenLocationContent={generateOpenLocationContentFor(
                                                                        location,
                                                                    )}
                                                                />
                                                            </GridItem>
                                                        )}
                                                        {/* XNG-Changes */}
                                                        {/* {location.phone && (
                                                            <GridItem {...styles.searchResultPhoneGridItem}>
                                                                <Link
                                                                    href={`tel:${location.phone}`}
                                                                    {...styles.searchResultPhoneLink}
                                                                    target="_blank"
                                                                >
                                                                    {location.phone}
                                                                </Link>
                                                            </GridItem>
                                                        )} */}
                                                    </GridContainer>
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                ))}
                            </StyledWrapper>
                        </FancyScroll>
                    </GridItem>
                    <GridItem {...styles.paginationGridItem}>
                        <LocationPagination
                            {...styles.pagination}
                            locations={locations}
                            locationsPagination={locationsPagination}
                            setPage={setPage}
                            setPageSize={setPageSize}
                        />
                    </GridItem>
                </GridContainer>
                <ContactUsFormModal location={contactUsLocationModal.location} handleClose={modalCloseHandler} />
            </StyledWrapper>
            <ShowroomModal
                isOpen={modalOpen.isOpen}
                dealer={modalOpen.dealer}
                modalCloseHandler={showroomModalCloseHandler}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationSearchForm);
